// ============================================================
// TMR Portal Admin — mapa da localização da Empresa
// Google Maps Embed API (iframe). Sem a chave, degrada para o
// endereço + link externo — a tela continua útil.
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { runtimeConfig } from '@tmr/auth';
import { Btn } from '../../components/primitives';
import { Icons, StrokeIcon } from '../../lib/icons';
import { companyAddress, mapsEmbedUrl, mapsExternalUrl } from '../../lib/format';
import type { AddressParts } from '../../lib/format';
import { OverlayPortal } from '../../components/OverlayPortal';

// Read at render, not at module load: in a container the value only exists once /config.js has run.
const embedKey = () => runtimeConfig('VITE_GOOGLE_MAPS_EMBED_KEY') ?? '';

/** Props for {@link CompanyMapModal}. */
interface CompanyMapModalProps {
  /** The Empresa's name, for the title. */
  name: string;
  /**
   * The Empresa's registered address. The list and the detail both carry it whole, so a `city` precision
   * here now means the record really is that incomplete — which is what the warning below says.
   */
  address: AddressParts | null | undefined;
  onClose: () => void;
}

export function CompanyMapModal({ name, address, onClose }: CompanyMapModalProps) {
  const [copied, setCopied] = useState(false);
  const { text, query, precision } = companyAddress(address);
  const stop = (e: MouseEvent) => e.stopPropagation();
  const key = embedKey();

  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 85, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 560, maxWidth: '100%', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <div className="pa-eyebrow" style={{ marginBottom: 4 }}>Localização</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>{name}</h3>
          </div>

          {precision === 'city' && (
            <div style={{ margin: '16px 20px 0', padding: 12, background: 'var(--cream-100)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, color: 'var(--fg-muted)' }}>
              Endereço completo não cadastrado — mostrando apenas a cidade.
            </div>
          )}

          {key ? (
            <div style={{ margin: '16px 20px 0', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--border-default)' }}>
              <iframe
                title={`Mapa da localização de ${name}`}
                src={mapsEmbedUrl(query, key)}
                width="100%"
                height="300"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          ) : (
            <div style={{ margin: '16px 20px 0', padding: 14, background: 'var(--cream-100)', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: 12.5, color: 'var(--fg-muted)' }}>
              Mapa embutido não configurado neste ambiente. Use "Abrir no Google Maps" abaixo.
            </div>
          )}

          <div style={{ padding: '16px 20px 4px' }}>
            <div style={{ fontSize: 11, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Endereço</div>
            <div style={{ fontSize: 14, color: 'var(--ink-900)', lineHeight: 1.5 }}>{text}</div>
          </div>

          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <a href={mapsExternalUrl(query)} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: 'var(--forest-800)', textDecoration: 'none', alignSelf: 'center' }}>
              <StrokeIcon d={Icons.external} size={15} /> Abrir no Google Maps
            </a>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="secondary" icon={copied ? Icons.check : Icons.copy} onClick={copy}>
                {copied ? 'Copiado' : 'Copiar endereço'}
              </Btn>
              <Btn variant="secondary" onClick={onClose}>
                Fechar
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
